import classnames from 'classnames';
import React, { useContext, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import { addVariants, transitionVariants } from '@agilemd/ui-utils';

import TabContext from '../Context.ts';
import styles from './styles.module.scss';

export const VARIANTS = ['default', 'vertical', 'horizontal'] as const;
export type PaneVariant = (typeof VARIANTS)[number];

export interface PaneProps {
  children: React.ReactNode | ((props: { setTab: (tab: unknown) => void }) => React.ReactNode);
  value: unknown;
  variant?: PaneVariant | boolean | PaneVariant[];
  className?: string;
  shouldRenderContent?: boolean;
}

const Pane = ({
  children,
  value,
  variant = 'default',
  className = '',
  shouldRenderContent = false,
  ...props
}: PaneProps) => {
  const { isSelected, setTab } = useContext(TabContext);
  const transitionRef = useRef<HTMLDivElement>(null);
  const isActive = isSelected(value);
  const paneContent = useMemo(() => {
    if (!isActive && !shouldRenderContent) {
      return null;
    }

    if (typeof children === 'function') {
      return children({ setTab });
    }

    return children;
  }, [children, setTab, isActive, shouldRenderContent]);

  return (
    <CSSTransition
      nodeRef={transitionRef}
      timeout={250}
      classNames={transitionVariants('aglmd-tabs__pane', styles)}
      in={isActive}
    >
      <div
        className={classnames(
          addVariants('aglmd-tabs__pane', styles, variant, isActive ? 'active' : 'inactive'),
          className
        )}
        {...props}
      >
        {paneContent}
      </div>
    </CSSTransition>
  );
};

export default Pane;
